// RouteErrorBoundary
// ------------------
// Wraps the dashboard's <DashboardRouter /> so a crash inside a single page
// component (e.g. Owners, FeeManagement) doesn't take down the entire
// dashboard shell — the user still has the header + sidebar and can navigate
// away. Resets automatically whenever `routeKey` changes.
import React from 'react';
import { Button, Result, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

const { Paragraph, Text } = Typography;

export interface RouteErrorBoundaryProps {
  /** Changing this value resets the boundary (call with the selected route key). */
  routeKey: string;
  children: React.ReactNode;
}

interface State {
  error: Error | null;
  info: React.ErrorInfo | null;
}

export class RouteErrorBoundary extends React.Component<RouteErrorBoundaryProps, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the stack around for the detail panel; log to console in dev.
    // eslint-disable-next-line no-console
    console.error('[RouteErrorBoundary] caught error rendering route:', error, info);
    this.setState({ info });
  }

  componentDidUpdate(prevProps: RouteErrorBoundaryProps) {
    // Reset when the user navigates to a different route.
    if (prevProps.routeKey !== this.props.routeKey && this.state.error) {
      this.setState({ error: null, info: null });
    }
  }

  private handleReset = () => this.setState({ error: null, info: null });

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <Result
        status="error"
        title="Something went wrong on this page"
        subTitle="The rest of the dashboard is fine — try reloading this view or pick a different section from the sidebar."
        extra={[
          <Button key="retry" type="primary" icon={<ReloadOutlined />} onClick={this.handleReset}>
            Retry
          </Button>,
          <Button key="reload" onClick={() => window.location.reload()}>Reload page</Button>,
        ]}
      >
        <div style={{ textAlign: 'left' }}>
          <Paragraph>
            <Text strong>Error:</Text> <Text code>{error.message}</Text>
          </Paragraph>
          {process.env.NODE_ENV !== 'production' && info?.componentStack && (
            <details style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 12, borderRadius: 6 }}>
              <summary style={{ cursor: 'pointer' }}>Component stack</summary>
              <Text code style={{ fontSize: 12 }}>{info.componentStack}</Text>
            </details>
          )}
        </div>
      </Result>
    );
  }
}

export default RouteErrorBoundary;
